/**
 * Timeline Controls
 *
 * UI controls for timeline visualization.
 * Handles filters, grouping, zoom, and interaction controls.
 *
 * @module timelineControls
 */

export class TimelineControls {
    constructor(timeline, options = {}) {
        this.timeline = timeline;
        this.options = {
            enableURLState: options.enableURLState !== false,
            ...options
        };

        console.log('TimelineControls initialized', this.options);
    }

    /**
     * Set up all control event listeners
     */
    setupControls() {
        this.setupFilterControls();
        this.setupGroupingControls();
        this.setupZoomControls();

        if (this.options.enableURLState) {
            this.loadStateFromURL();
        }

        console.log('Timeline controls set up');
    }

    /**
     * Set up object count filter controls
     */
    setupFilterControls() {
        const filterSelect = document.getElementById('timelineObjects');
        if (filterSelect) {
            filterSelect.addEventListener('change', (e) => {
                console.log('Object count filter changed:', e.target.value);
                this.timeline.updateTimeline({ objectCount: e.target.value });

                if (this.options.enableURLState) {
                    this.saveStateToURL();
                }
            });
            console.log('Object count filter attached to #timelineObjects');
        } else {
            console.warn('Object count filter control #timelineObjects not found');
        }
    }

    /**
     * Set up grouping controls
     */
    setupGroupingControls() {
        const groupingSelect = document.getElementById('timelineGrouping');
        if (groupingSelect) {
            groupingSelect.addEventListener('change', (e) => {
                console.log('Grouping changed:', e.target.value);
                this.timeline.updateTimeline({ grouping: e.target.value });

                if (this.options.enableURLState) {
                    this.saveStateToURL();
                }
            });
            console.log('Grouping control attached to #timelineGrouping');
        } else {
            console.warn('Grouping control #timelineGrouping not found');
        }
    }

    /**
     * Set up zoom controls
     */
    setupZoomControls() {
        // Zoom in button
        const zoomInBtn = document.getElementById('zoomIn');
        if (zoomInBtn) {
            zoomInBtn.addEventListener('click', () => {
                this.timeline.zoomTimeline(1.2);
            });
        }

        // Zoom out button
        const zoomOutBtn = document.getElementById('zoomOut');
        if (zoomOutBtn) {
            zoomOutBtn.addEventListener('click', () => {
                this.timeline.zoomTimeline(0.8);
            });
        }

        // Reset view button
        const resetBtn = document.getElementById('resetView');
        if (resetBtn) {
            resetBtn.addEventListener('click', () => {
                this.timeline.resetZoom();
            });
        }
    }


    /**
     * Load timeline state from URL parameters
     */
    loadStateFromURL() {
        const urlParams = new URLSearchParams(window.location.search);

        const state = {
            objectCount: urlParams.get('timeline_count'),
            grouping: urlParams.get('timeline_grouping')
        };

        // Only update if values are present
        const updates = {};
        if (state.objectCount) updates.objectCount = state.objectCount;
        if (state.grouping) updates.grouping = state.grouping;

        if (Object.keys(updates).length > 0) {
            console.log('Loading timeline state from URL:', updates);
            this.timeline.updateTimeline(updates);

            // Update UI controls
            this.updateControlsUI(updates);
        }
    }

    /**
     * Save timeline state to URL parameters
     */
    saveStateToURL() {
        const urlParams = new URLSearchParams(window.location.search);

        const options = this.timeline.options;

        // Update URL parameters
        if (options.objectCount) {
            urlParams.set('timeline_count', options.objectCount);
        }
        if (options.grouping) {
            urlParams.set('timeline_grouping', options.grouping);
        }

        // Update URL without reloading page
        const newURL = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, '', newURL);

        console.log('Timeline state saved to URL');
    }

    /**
     * Update UI controls to match state
     * @param {Object} state - State values
     */
    updateControlsUI(state) {
        if (state.objectCount) {
            const filterControl = document.getElementById('timelineObjects');
            if (filterControl) {
                filterControl.value = state.objectCount;
            }
        }

        if (state.grouping) {
            const groupingControl = document.getElementById('timelineGrouping');
            if (groupingControl) {
                groupingControl.value = state.grouping;
            }
        }
    }

    /**
     * Reset all controls to defaults
     */
    reset() {
        this.timeline.updateTimeline({
            objectCount: 'top10',
            grouping: 'score'
        });

        this.updateControlsUI({
            objectCount: 'top10',
            grouping: 'score'
        });

        this.timeline.resetZoom();

        console.log('Timeline controls reset to defaults');
    }

    /**
     * Clean up and remove event listeners
     */
    destroy() {
        console.log('TimelineControls destroyed');
    }
}

export default TimelineControls;
